import React from "react";
import {
  Paper,
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  Autocomplete,
  Chip,
  Avatar,
  InputAdornment,
  Divider,
} from "@mui/material";

import { makeStyles } from "@mui/styles";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";

import Helper from "../../common/Helper";

const useStyles = makeStyles((theme) => ({
  page: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    minHeight: "calc(100vh - 192px)",
    background:
      "linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #eef2ff 100%)",
  },

  card: {
    width: "100%",
    maxWidth: 1000,
    padding: 40,
    borderRadius: 24,
    position: "relative",
    boxShadow: "0 25px 70px rgba(0,0,0,0.15)",
  },

  modeIcon: {
    position: "absolute",
    top: -16,
    left: -16,
    width: 50,
    height: 50,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
    border: "2px solid #e5e7eb",
  },

  header: {
    textAlign: "center",
    marginBottom: 30,
  },

  title: {
    fontWeight: 700,
    fontSize: 22,
  },

  subtitle: {
    color: "#64748b",
    fontSize: 13,
  },

  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      transition: "0.3s",
      "&:hover": {
        boxShadow: "0 0 0 3px rgba(99,102,241,0.08)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 4px rgba(59,130,246,0.15)",
      },
    },
  },

  teamSection: {
    padding: 20,
    borderRadius: 18,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },

  teamTitle: {
    fontWeight: 700,
    fontSize: 16,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#1e293b",
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 10,
  },

  saveBtn: {
    borderRadius: 12,
    fontWeight: 600,
    color: "#fff",
    background: (props) =>
      props.isEditMode
        ? "linear-gradient(135deg, #1d428a, #2c5cc5)"
        : "linear-gradient(45deg, #22c55e, #16a34a)",
    padding: "10px 20px",
    transition: "0.3s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
    },
  },

  cancelBtn: {
    borderRadius: 12,
    fontWeight: 600,
    border: "1px solid #cbd5e1",
    color: "#475569",
    "&:hover": {
      background: "#f1f5f9",
    },
  },

  chip: {
    transition: "0.3s",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
}));

export default function GameFormCard({
  isEditMode,
  form,
  setForm,
  playersList,
  handleSubmit,
  handleCancel,
}) {
  const classes = useStyles({ isEditMode });

  //... SAFETY CHECKS
  const team1Players = form.team1Players || [];
  const team2Players = form.team2Players || [];

  const team1Options = playersList.filter(
    (p) => !team2Players.some((t) => t.id === p.id)
  );

  const team2Options = playersList.filter(
    (p) => !team1Players.some((t) => t.id === p.id)
  );

  const renderPlayerTags = (value, getTagProps) =>
    value.map((option, index) => {
      const { key, ...chipProps } = getTagProps({ index });

      return (
        <Chip
          key={key}
          {...chipProps}
          label={option.name}
          avatar={
            <Avatar src={option.headShotUrl} sx={{ bgcolor: "#1d428a" }}>
              {Helper.GetNameInitials(option.name)}
            </Avatar>
          }
          className={classes.chip}
          sx={{
            "& .MuiChip-avatar": {
              color: "#fff",
              fontWeight: 600,
            },
          }}
        />
      );
    });

  return (
    <Box className={classes.page}>
      <Paper className={classes.card} elevation={10}>
        <Box className={classes.modeIcon}>
          {isEditMode ? (
            <EditOutlinedIcon sx={{ color: "#6366f1" }} />
          ) : (
            <AddCircleOutlineIcon sx={{ color: "#22c55e" }} />
          )}
        </Box>

        <Box className={classes.header}>
          <SportsEsportsIcon sx={{ fontSize: 42, color: "#3b82f6" }} />
          <Typography className={classes.title}>
            {isEditMode ? "Edit Game" : "Create Game"}
          </Typography>
          <Typography className={classes.subtitle}>
            Manage game details & teams
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Game Details */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Game Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={classes.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Game URL"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className={classes.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Team 1 */}
          <Grid item xs={12} md={6}>
            <Box className={classes.teamSection}>
              <Typography className={classes.teamTitle}>
                <GroupsIcon color="primary" />
                Team 1
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Team 1 Name"
                    value={form.team1Name || ""}
                    onChange={(e) =>
                      setForm({ ...form, team1Name: e.target.value })
                    }
                    className={classes.input}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={team1Options}
                    value={team1Players}
                    onChange={(e, v) => setForm({ ...form, team1Players: v })}
                    getOptionLabel={(o) => o?.name || ""}
                    renderTags={renderPlayerTags}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tagged Players"
                        className={classes.input}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Team 2 */}
          <Grid item xs={12} md={6}>
            <Box className={classes.teamSection}>
              <Typography className={classes.teamTitle}>
                <GroupsIcon sx={{ color: "#ef4444" }} />
                Team 2
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Team 2 Name"
                    value={form.team2Name || ""}
                    onChange={(e) =>
                      setForm({ ...form, team2Name: e.target.value })
                    }
                    className={classes.input}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={team2Options}
                    value={team2Players}
                    onChange={(e, v) => setForm({ ...form, team2Players: v })}
                    getOptionLabel={(o) => o?.name || ""}
                    renderTags={renderPlayerTags}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tagged Players"
                        className={classes.input}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box className={classes.actions}>
              <Button className={classes.cancelBtn} onClick={handleCancel}>
                Cancel
              </Button>

              <Button className={classes.saveBtn} onClick={handleSubmit}>
                {isEditMode ? "Update Game" : "Save Game"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
